import type { components, paths } from "admination-react/api-schema";
import {
  fillPath,
  getJson,
  getJsonOrNull,
  sendDelete,
  sendJson,
  type QueryValue,
  type WriteIdentity,
} from "./http";

type Op<P extends keyof paths, M extends string> = paths[P] extends Record<M, infer O> ? O : never;

type QueryOf<O> = O extends { parameters: { query?: infer Q } } ? Q : never;
type PathParamsOf<O> = O extends { parameters: { path: infer P } } ? P : never;
type JsonBodyOf<O> = O extends { requestBody: { content: { "application/json": infer B } } }
  ? B
  : never;
type JsonOkOf<O> = O extends { responses: { 200: { content: { "application/json": infer B } } } }
  ? B
  : O extends { responses: { 201: { content: { "application/json": infer B } } } }
    ? B
    : never;

function asQuery(query: unknown): Record<string, QueryValue> | undefined {
  return query as Record<string, QueryValue> | undefined;
}

function asPathParams(params: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries((params ?? {}) as Record<string, unknown>)) {
    out[key] = String(value);
  }
  return out;
}

export const PAGE_LIMIT = 200;

export function apiList<P extends keyof paths>(
  path: P,
  query?: QueryOf<Op<P, "get">>
): Promise<JsonOkOf<Op<P, "get">>> {
  return getJson(path as string, asQuery(query));
}

// A short page proves completeness; the ceiling throws instead of returning a truncated set.
export async function apiListAll<P extends keyof paths>(
  path: P,
  query?: QueryOf<Op<P, "get">>,
  maxPages = 25,
  onFirstPageHeaders?: (headers: Headers) => void,
): Promise<JsonOkOf<Op<P, "get">>> {
  const base = { ...(asQuery(query) ?? {}) };
  const rows: unknown[] = [];
  for (let page = 0; page < maxPages; page += 1) {
    const chunk = (await getJson(path as string, {
      ...base,
      limit: PAGE_LIMIT,
      offset: page * PAGE_LIMIT,
    }, page === 0 ? onFirstPageHeaders : undefined)) as unknown[];
    rows.push(...chunk);
    if (chunk.length < PAGE_LIMIT) return rows as JsonOkOf<Op<P, "get">>;
  }
  throw new Error(
    `${String(path)} returned more than ${maxPages * PAGE_LIMIT} rows — narrow the query`
  );
}

export function newestLive<R extends { created_at: string; ersetzt_am: string | null }>(
  rows: readonly R[],
): R | null {
  return (
    rows
      .filter((r) => !r.ersetzt_am)
      .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0] ?? null
  );
}

export function apiGetOne<P extends keyof paths>(
  template: P,
  params: PathParamsOf<Op<P, "get">>,
  query?: QueryOf<Op<P, "get">>
): Promise<JsonOkOf<Op<P, "get">>> {
  return getJson(fillPath(template as string, asPathParams(params)), asQuery(query));
}

export function apiGetOneOrNull<P extends keyof paths>(
  template: P,
  params: PathParamsOf<Op<P, "get">>,
  query?: QueryOf<Op<P, "get">>
): Promise<JsonOkOf<Op<P, "get">> | null> {
  return getJsonOrNull(fillPath(template as string, asPathParams(params)), asQuery(query));
}

export async function apiFirst<P extends keyof paths>(
  path: P,
  query?: QueryOf<Op<P, "get">>
): Promise<(JsonOkOf<Op<P, "get">> extends readonly (infer R)[] ? R : never) | null> {
  const rows = (await apiList(path, query)) as unknown[];
  return (rows[0] ?? null) as never;
}

export function apiPost<P extends keyof paths>(
  path: P,
  body: JsonBodyOf<Op<P, "post">>,
  identity?: WriteIdentity,
): Promise<JsonOkOf<Op<P, "post">>> {
  return sendJson(path as string, "POST", body, undefined, identity);
}

export function apiPut<P extends keyof paths>(
  path: P,
  body: JsonBodyOf<Op<P, "put">>,
  identity?: WriteIdentity,
): Promise<JsonOkOf<Op<P, "put">>> {
  return sendJson(path as string, "PUT", body, undefined, identity);
}

export function apiPatch<P extends keyof paths>(
  template: P,
  params: PathParamsOf<Op<P, "patch">>,
  body: JsonBodyOf<Op<P, "patch">>,
  identity?: WriteIdentity,
): Promise<JsonOkOf<Op<P, "patch">>> {
  return sendJson(fillPath(template as string, asPathParams(params)), "PATCH", body, undefined, identity);
}

export function apiDelete<P extends keyof paths>(
  template: P,
  params: PathParamsOf<Op<P, "delete">>,
  identity?: WriteIdentity,
): Promise<void> {
  return sendDelete(fillPath(template as string, asPathParams(params)), undefined, identity);
}

type Schemas = components["schemas"];
export type Participant = Pick<Schemas["TeilnehmendeRead"],
  "vorname" | "nachname" | "kundennummer" | "geburtsdatum" | "email" | "phone" | "strasse" | "hausnummer" | "plz" | "ort">;
export type CoachingRow = Schemas["CoachingCoreRead"];
export type MassnahmeRow = Schemas["MassnahmeCoreRead"];

export type MassnahmeCoreWrite = Schemas["MassnahmeCoreWrite"];
export type MassnahmeCreate = Partial<Omit<Schemas["MassnahmeAzavWrite"], "organisation_id" | "variant">>;
